import type { Cell, Row } from '@tanstack/react-table';
import type { DataSchema, Schema } from 'types';
import type { HeliosCellData } from 'types/helios/agendaListing';
import type { FormItemUiDefinition } from 'types/formItem';
import type { HeliosRecordDataTableColumnFormatting } from 'types/dataHelios';
import { getIcon } from 'utils/common';
import { getUiDefinitionContainerStyles, getUiDefinitionTextStyles } from 'utils/styles';
import { renderLinkSafelyOrNull } from 'utils/renderes/renderLinkSafelyOrNull';
import { replaceKey } from 'utils/formItemsDots';
import { formatHeliosDateValue } from 'utils/dateAndTime';

type ColumnMeta<P extends DataSchema> = {
  uiDefinition: P;
};

export type CellTextAlign = HeliosRecordDataTableColumnFormatting['formatValue']['textAlign'];

export const getHeliosCell = <T extends keyof P, P extends DataSchema>({
  cell,
  row,
  dataSchema,
}: {
  cell: Cell<Schema<T, P>, unknown>;
  row: Row<Schema<T, P>>;
  dataSchema: P;
}) => {
  const cellKey = cell.column.id;

  const getCellValue = () => {
    let value = cell.getValue() as { rawValue: string | number | { id: number; name?: string }; unit: string | null } | null;
    const hasSpecialChars = /[.\-\s+*/%@#$!?:;()[\]{}]/.test(cellKey);
    if (!value && hasSpecialChars) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value = (row.original as any)[cellKey] ?? null;
    }
    return value;
  };

  const cellValue = getCellValue();

  const cellData = cell.getValue() as HeliosCellData;

  let displayValue = cellValue?.unit ? `${cellValue.rawValue} ${cellValue.unit}` : cellValue?.rawValue?.toString() || '';
  if (typeof cellValue?.rawValue === 'object' && cellValue?.rawValue !== null && 'name' in cellValue.rawValue) {
    displayValue = cellValue.rawValue.name || cellValue.rawValue.id?.toString() || '';
  }

  const udpType = cellData?.userDefinedAttributeType;
  const columnFormatVariant = cellData?.formatting?.formatValue?.typeSpecific?.formatDateTime?.variant;
  const dateFormatVariant: 'Date' | 'DateTime' | 'Time' | 'MonthYear' | null = columnFormatVariant ?? udpType ?? null;

  if (dateFormatVariant) {
    const rawStr = typeof cellValue?.rawValue === 'string' ? cellValue.rawValue : null;
    const formattedDate = formatHeliosDateValue(rawStr, dateFormatVariant, 'dd.MM.yyyy HH:mm');

    if (formattedDate) {
      displayValue = formattedDate;
    }
  }

  const cellKeySubstituted = replaceKey(cellKey);
  const schemaEntryValue = dataSchema[cellKeySubstituted];

  const typeSpecific = (
    schemaEntryValue as {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatting?: { formatValue?: { typeSpecific?: any } };
    }
  )?.formatting?.formatValue?.typeSpecific;

  const isWebLink = Boolean(typeSpecific?.formatText?.showWebLink);
  const isPhoneOrEmail = Boolean(typeSpecific?.formatText?.showPhoneOrMail);
  const isTextArea = Boolean(typeSpecific?.formatText?.showAsTextArea);
  const isRichText = Boolean(typeSpecific?.formatText?.showAsRichText);
  const uiDefinition: FormItemUiDefinition | undefined = cellData?.uiDefinition ?? (cell.column.columnDef.meta as ColumnMeta<P>)?.uiDefinition;

  const Icon = uiDefinition?.icon ? getIcon(uiDefinition.icon) : null;

  const boxStyles = getUiDefinitionContainerStyles(uiDefinition);
  const textStyles = getUiDefinitionTextStyles(uiDefinition);

  const valueTextAlign: CellTextAlign | undefined =
    cellData?.formatting?.formatValue?.textAlign ??
    (schemaEntryValue as { formatting?: { formatValue?: { textAlign?: CellTextAlign } } })?.formatting?.formatValue?.textAlign;

  const labelTextAlign: CellTextAlign | undefined =
    cellData?.formatting?.formatLabel?.textAlign ??
    (schemaEntryValue as { formatting?: { formatLabel?: { textAlign?: CellTextAlign } } })?.formatting?.formatLabel?.textAlign;

  const getLink = () => {
    if (typeof displayValue !== 'string' || !displayValue) return null;
    if (isWebLink || isPhoneOrEmail) {
      return renderLinkSafelyOrNull(displayValue, isPhoneOrEmail);
    }
    return null;
  };

  const link = getLink();

  const valueForRender = link ? link : displayValue;

  return {
    displayValue,
    valueForRender,
    uiDefinition,
    Icon,
    boxStyles,
    textStyles,
    isTextArea,
    isRichText,
    valueTextAlign,
    labelTextAlign,
  };
};
